import { useState, useEffect, useCallback, useMemo } from "react";
import { playListService } from "../services/playList";
import logger from '../utils/logger';

/**
 * usePlayList - Manage the user's Play List state.
 *
 * @param {Object|null} user - Current user object (anonymous if null/undefined).
 * @returns {Object} {
 *   playList: Array,
 *   count: number,
 *   loading: boolean,
 *   error: string|null,
 *   isInPlayList: (gameId: number) => boolean,
 *   addToPlayList: (game: object) => Promise<void>,
 *   removeFromPlayList: (gameId: number) => Promise<void>,
 *   clearPlayList: () => Promise<void>,
 *   loadPlayList: () => Promise<void>,
 * }
 */
export function usePlayList(user) {
  const [playList, setPlayList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isAuthenticated = Boolean(user && user.id);

  const normalizeGameId = useCallback((game) => {
    return game.game_id || game.id;
  }, []);

  const loadPlayList = useCallback(async () => {
    if (!isAuthenticated) {
      setPlayList([]);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await playListService.fetchPlayList();
      setPlayList(Array.isArray(data) ? data : []);
    } catch (err) {
      logger.error("Failed to load Play List:", err);
      setError(err.message || "Failed to load Play List");
      setPlayList([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadPlayList();
  }, [loadPlayList]);

  const isInPlayList = useCallback((gameId) => {
    if (!gameId) return false;
    return playList.some((game) => game.game_id === gameId || game.id === gameId);
  }, [playList]);

  const addToPlayList = useCallback(async (game) => {
    if (!isAuthenticated) {
      logger.debug("Cannot add to Play List: user is not authenticated");
      return;
    }

    const gameId = normalizeGameId(game);
    if (!gameId) {
      logger.warn("Cannot add to Play List: game has no id");
      return;
    }

    // Optimistic update
    setPlayList((prev) => {
      if (prev.some((g) => g.game_id === gameId || g.id === gameId)) {
        return prev;
      }
      return [
        {
          game_id: gameId,
          id: gameId,
          name: game.name,
          thumbnail: game.thumbnail,
          year_published: game.year_published,
        },
        ...prev,
      ];
    });

    try {
      await playListService.addToPlayList(gameId);
      await loadPlayList();
    } catch (err) {
      logger.error("Failed to add game to Play List:", err);
      // Revert optimistic update on failure
      setPlayList((prev) => prev.filter((g) => (g.game_id || g.id) !== gameId));
      setError(err.message || "Failed to add game to Play List");
    }
  }, [isAuthenticated, loadPlayList, normalizeGameId]);

  const removeFromPlayList = useCallback(async (gameId) => {
    if (!isAuthenticated || !gameId) return;

    // Optimistic update
    setPlayList((prev) => prev.filter((g) => (g.game_id || g.id) !== gameId));

    try {
      await playListService.removeFromPlayList(gameId);
    } catch (err) {
      logger.error("Failed to remove game from Play List:", err);
      // Revert on failure
      await loadPlayList();
      setError(err.message || "Failed to remove game from Play List");
    }
  }, [isAuthenticated, loadPlayList]);

  const clearPlayList = useCallback(async () => {
    if (!isAuthenticated) return;

    // Optimistic update
    setPlayList([]);

    try {
      await playListService.clearPlayList();
    } catch (err) {
      logger.error("Failed to clear Play List:", err);
      await loadPlayList();
      setError(err.message || "Failed to clear Play List");
    }
  }, [isAuthenticated, loadPlayList]);

  const count = useMemo(() => playList.length, [playList]);

  return {
    playList,
    count,
    loading,
    error,
    isInPlayList,
    addToPlayList,
    removeFromPlayList,
    clearPlayList,
    loadPlayList,
  };
}

export default usePlayList;
